/**
 * Rezept-Upload widget for the storefront theme.
 *
 * The interface and the add-to-cart rules are the ones from the classic `product-file-upload`
 * snippet — pick files, see 80px preview tiles, remove one with the red badge, and a cart that
 * only accepts a prescription file or a typed note. What changed is where the bytes go: the
 * customer's file never touches Shopify and never touches this app's server. The theme asks the
 * app for a one-shot signed URL, PUTs the bytes straight to the private S3 bucket, then tells the
 * app the upload finished. What ends up in the cart is an opaque token — never a URL, never a
 * filename.
 *
 * Everything validated here is validated again on the server. Client-side checks exist so a
 * customer on a phone learns "zu groß" before spending 30 seconds uploading, not instead of the
 * server saying so.
 *
 * No dependencies, no build step: this file is dropped into the theme's assets/ folder as-is.
 * XMLHttpRequest rather than fetch, because fetch still cannot report upload progress and the
 * customers here are mostly on mobile connections where progress is the difference between
 * waiting and reloading the page.
 */
(function () {
  'use strict'

  var SELECTOR = '[data-prescription-upload]'

  var MESSAGES = {
    consentRequired: 'Bitte stimmen Sie der Verarbeitung Ihres Rezepts zu.',
    tooMany: 'Es können höchstens {max} Rezepte pro Produkt hochgeladen werden.',
    tooLarge: 'Die Datei „{name}" ist größer als {max} MB.',
    badType: 'Die Datei „{name}" ist kein Foto (JPG, PNG, HEIC) und kein PDF.',
    empty: 'Die Datei „{name}" ist leer.',
    network: 'Die Verbindung wurde unterbrochen. Bitte versuchen Sie es erneut.',
    server: 'Der Upload ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    busy: 'Ihr Rezept wird noch hochgeladen. Einen Moment bitte.',
    notAttached: 'Ihr Rezept konnte nicht an die Bestellung angehängt werden. Bitte laden Sie die Seite neu.',
    uploading: 'Rezept wird hochgeladen …',
    verifying: 'Rezept wird geprüft …',
    done: 'Rezept erfolgreich übermittelt.',
    remove: 'Entfernen',
    retry: 'Erneut versuchen',
    fileOne: '1 Datei',
    fileMany: '{count} Dateien'
  }

  var EXTENSION_TYPES = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    heic: 'image/heic',
    heif: 'image/heic',
    pdf: 'application/pdf'
  }

  function isPdf(entry) {
    return entry.contentType === 'application/pdf'
  }

  /**
   * The document tile stands in for anything that cannot be shown as a picture. It used to say
   * "PDF" whatever it was given, which turned every iPhone HEIC photo into a PDF in front of the
   * customer — so the label comes from the file itself now.
   *
   * The label is stripped to letters and digits before it goes into the SVG: it comes from a file
   * name, which is customer input, and this is the one place here that builds markup as a string.
   */
  function documentIcon(entry) {
    var pdf = isPdf(entry)
    var parts = entry.file.name.split('.')
    var extension = parts.length > 1 ? parts[parts.length - 1] : ''
    var label = (pdf ? 'PDF' : extension || 'Datei').replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase() || 'DATEI'

    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="48" height="48" aria-hidden="true">' +
      '<rect x="8" y="2" width="40" height="52" rx="4" fill="' + (pdf ? '#e53935' : '#546e7a') + '"/>' +
      '<rect x="8" y="2" width="28" height="52" rx="4" fill="' + (pdf ? '#ef5350' : '#78909c') + '"/>' +
      '<polygon points="36,2 48,14 36,14" fill="' + (pdf ? '#ffcdd2' : '#cfd8dc') + '"/>' +
      '<polygon points="36,2 48,14 36,14" fill="' + (pdf ? '#b71c1c' : '#263238') + '" opacity="0.3"/>' +
      '<text x="32" y="40" text-anchor="middle" font-size="' + (label.length > 3 ? 11 : 14) + '" font-weight="bold" fill="#fff" font-family="Arial">' +
      label +
      '</text>' +
      '</svg>'
    )
  }

  function format(template, values) {
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
    })
  }

  /**
   * iOS hands back an empty `type` for HEIC often enough that trusting it alone loses real
   * uploads. The extension is only a hint either way — the server reads the magic bytes after the
   * file lands, and that is the type that decides.
   */
  function resolveContentType(file) {
    var declared = (file.type || '').toLowerCase()

    if (EXTENSION_TYPES[declared.split('/')[1]] === declared) {
      return declared
    }

    var parts = file.name.toLowerCase().split('.')
    var extension = parts.length > 1 ? parts[parts.length - 1] : ''

    return EXTENSION_TYPES[extension] || declared
  }

  function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID()
    }

    if (window.crypto && window.crypto.getRandomValues) {
      var bytes = new Uint8Array(16)
      window.crypto.getRandomValues(bytes)

      return Array.prototype.map
        .call(bytes, function (byte) {
          return ('0' + byte.toString(16)).slice(-2)
        })
        .join('')
    }

    return 'rx-' + Date.now() + '-' + Math.random().toString(36).slice(2, 12)
  }

  /**
   * One id per product per visit — the key comes from the snippet, which scopes it to `product.id`
   * — so the server's file cap counts the same thing the customer sees. A single shared id would
   * make the cap span the whole visit: two prescription products in one order, and the second one
   * could not be uploaded for at all.
   *
   * sessionStorage rather than a fresh id per page load: a customer who reloads the product page
   * mid-checkout has not started a new order, and issuing a new id there would silently hand them
   * a fresh set of upload slots.
   */
  function getUploadSessionId(storageKey) {
    var stored = null

    try {
      stored = window.sessionStorage.getItem(storageKey)
    } catch (error) {
      // Private mode on iOS throws on access. A per-page id still works; it only makes the cap
      // more generous, and the server is the one enforcing it.
    }

    if (stored) {
      return stored
    }

    var created = randomId()

    try {
      window.sessionStorage.setItem(storageKey, created)
    } catch (error) {
      /* see above */
    }

    return created
  }

  function parseErrorMessage(responseText, fallback) {
    try {
      var payload = JSON.parse(responseText)

      if (payload && payload.error && payload.error.message) {
        return payload.error.message
      }
    } catch (error) {
      /* not JSON — fall through */
    }

    return fallback
  }

  function request(method, url, options) {
    var settings = options || {}

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest()

      xhr.open(method, url, true)
      xhr.timeout = settings.timeout || 120000

      Object.keys(settings.headers || {}).forEach(function (header) {
        xhr.setRequestHeader(header, settings.headers[header])
      })

      if (settings.onProgress && xhr.upload) {
        xhr.upload.onprogress = function (event) {
          if (event.lengthComputable) {
            settings.onProgress(event.loaded / event.total)
          }
        }
      }

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.responseText)

          return
        }

        reject(new Error(parseErrorMessage(xhr.responseText, MESSAGES.server)))
      }

      // A dropped mobile connection and a timeout look the same to the customer: the upload did
      // not finish and retrying is the right move.
      xhr.onerror = function () {
        reject(new Error(MESSAGES.network))
      }
      xhr.ontimeout = xhr.onerror
      xhr.onabort = function () {
        reject(new Error(MESSAGES.network))
      }

      settings.onStart && settings.onStart(xhr)

      xhr.send(settings.body || null)
    })
  }

  function PrescriptionUpload(root) {
    this.root = root
    this.endpoint = (root.getAttribute('data-endpoint') || '').replace(/\/$/, '')
    this.propertyName = root.getAttribute('data-property') || '_prescription_token'
    this.maxFiles = parseInt(root.getAttribute('data-max-files'), 10) || 2
    this.maxBytes = parseInt(root.getAttribute('data-max-bytes'), 10) || 5 * 1024 * 1024
    this.consentVersion = root.getAttribute('data-consent-version') || ''
    this.statusProperty = root.getAttribute('data-status-property') || ''
    this.statusText = root.getAttribute('data-status-text') || ''
    this.emptyLabel = root.getAttribute('data-text-chosen') || 'Keine Datei ausgewählt'
    this.uploadSessionId = getUploadSessionId(root.getAttribute('data-session-key') || 'rx-upload-session')

    this.input = root.querySelector('[data-prescription-input]')
    this.consent = root.querySelector('[data-prescription-consent]')
    this.list = root.querySelector('[data-prescription-list]')
    this.status = root.querySelector('[data-prescription-status]')
    this.trigger = root.querySelector('[data-prescription-trigger]')
    this.fileName = root.querySelector('[data-prescription-filename]')
    this.error = root.querySelector('[data-prescription-error]')

    // The snippet is usually rendered inside the product form, but themes move things around. Fall
    // back to the page's add-to-cart form so the gate keeps working either way.
    this.form = root.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]')

    this.entries = []

    this.detachInput()
    this.bind()
    this.render()
  }

  /**
   * Moves the file picker out of the add-to-cart form, and only then makes it a file picker.
   *
   * theme.js still carries the old Shopify-upload gate: any `input[type="file"]` inside the
   * product form makes `cartAddEvent` bail out — silently, no message — unless that input holds a
   * file or the note textarea is filled. Our bytes go to S3, so the input is empty by design and
   * add-to-cart would do nothing at all. Parking the input on <body> takes that branch out of the
   * picture; the picker still opens from the trigger button, and the widget's own gate is then the
   * only thing deciding whether a prescription is present.
   *
   * The snippet ships the input without a type for the same reason: markup rendered where this
   * script never runs — a quick-add modal, a JS failure — must not leave a file input sitting in a
   * cart form, or add-to-cart dies on that page with nothing on screen to explain it.
   */
  PrescriptionUpload.prototype.detachInput = function () {
    if (!this.input) {
      return
    }

    this.input.setAttribute('data-prescription-detached', '')
    this.input.__prescriptionRoot = this.root
    document.body.appendChild(this.input)

    this.input.setAttribute('accept', this.input.getAttribute('data-prescription-accept') || '')
    this.input.multiple = true
    this.input.type = 'file'
  }

  PrescriptionUpload.prototype.bind = function () {
    var self = this

    if (this.trigger && this.input) {
      this.trigger.addEventListener('click', function (event) {
        event.preventDefault()
        self.input.click()
      })
    }

    if (this.input) {
      this.input.addEventListener('change', function () {
        self.handleFiles(Array.prototype.slice.call(self.input.files || []))
        // Reset so picking the same file twice still fires a change event.
        self.input.value = ''
      })
    }

    if (this.list) {
      // Delegated, because the tiles are rebuilt on every state change and per-tile listeners
      // would have to be rebound each time.
      this.list.addEventListener('click', function (event) {
        var entry = self.entryFor(event.target)

        if (!entry) {
          return
        }

        if (event.target.closest('[data-prescription-remove]')) {
          event.preventDefault()
          self.removeEntry(entry)

          return
        }

        if (event.target.closest('[data-prescription-retry]')) {
          event.preventDefault()
          self.upload(entry)
        }
      })
    }

    if (this.consent) {
      this.consent.addEventListener('change', function () {
        self.renderGate()
      })
    }

    if (this.form) {
      // Capture phase, so the submit is stopped before any theme handler turns it into a fetch to
      // /cart/add.js. A keyboard submit never goes through the button's click handler below.
      this.form.addEventListener(
        'submit',
        function (event) {
          if (self.allowAddToCart()) {
            return
          }

          event.preventDefault()
          event.stopPropagation()
        },
        true
      )
    }

    this.bindAddToCart()
  }

  /**
   * Blocks the add-to-cart click while the requirement is unmet — the rule `product-file-upload`
   * enforced, moved one phase earlier.
   *
   * Capture phase on the document, so this runs before <product-form>'s own click handler no
   * matter which script loaded first, and stopImmediatePropagation keeps that handler from firing
   * `theme:cart:add`. Dispatching that event here as well, the way the old snippet did, would mean
   * two add requests for one click; the theme already dispatches it, so an unblocked click is all
   * that is needed.
   */
  PrescriptionUpload.prototype.bindAddToCart = function () {
    var self = this

    document.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest && event.target.closest('[data-add-to-cart]')

        if (!button || (self.form && !self.form.contains(button))) {
          return
        }

        if (self.allowAddToCart()) {
          return
        }

        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()

        self.releaseCartBar()
      },
      true
    )
  }

  /**
   * The sticky cart bar marks its own button loading and disabled *before* forwarding the click to
   * the real add-to-cart button, and only `theme:product:added` / `theme:product:add-error` undoes
   * that. Blocking the click means neither ever fires, so without this the bar's button stays
   * spinning and unclickable until the page is reloaded — the customer's only add-to-cart on
   * mobile, dead.
   */
  PrescriptionUpload.prototype.releaseCartBar = function () {
    var bar = document.querySelector('[data-add-to-cart-bar]')

    if (!bar) {
      return
    }

    // The loading class is what tells the two cases apart: the cart bar sets it together with
    // `disabled` a moment before forwarding the click. A button disabled for a sold-out variant
    // never carries it, and must stay locked.
    if (!bar.classList.contains('is-loading')) {
      return
    }

    bar.classList.remove('is-loading')
    bar.removeAttribute('disabled')
  }

  PrescriptionUpload.prototype.noteValue = function () {
    var note = (this.form && this.form.querySelector('textarea.properties__input')) || document.querySelector('textarea.properties__input')

    return note ? note.value.trim() : ''
  }

  /**
   * The rule from `product-file-upload`: a prescription file or a typed note, one of the two. What
   * counts as "a file" here is a file the server has confirmed — a tile whose upload is still
   * running is not a prescription the pharmacy can dispense from.
   */
  PrescriptionUpload.prototype.allowAddToCart = function () {
    if (this.isBusy()) {
      this.announce(MESSAGES.busy, 'info')

      return false
    }

    if (!this.hasVerifiedFile() && this.noteValue() === '') {
      this.showError(true)

      return false
    }

    if (this.hasVerifiedFile() && !this.isConsentGiven()) {
      this.announce(MESSAGES.consentRequired, 'error')

      return false
    }

    if (this.hasVerifiedFile()) {
      // Written again here, immediately before the theme reads `new FormData(form)`. The inputs
      // are normally in place from the moment the upload was confirmed, but anything that
      // re-renders the product form in between — a variant change, a section reload — throws them
      // away, and the customer would then check out with no token on the line item.
      this.syncProperties()

      // Nothing to lose quietly: an upload the customer was told succeeded must not travel as an
      // order that carries no reference to it.
      if (!this.form || this.form.querySelectorAll('[data-prescription-token]').length === 0) {
        this.announce(MESSAGES.notAttached, 'error')

        return false
      }
    }

    this.showError(false)

    return true
  }

  PrescriptionUpload.prototype.entryFor = function (target) {
    var item = target.closest && target.closest('[data-entry-id]')

    if (!item) {
      return null
    }

    var id = item.getAttribute('data-entry-id')

    return (
      this.entries.filter(function (entry) {
        return entry.id === id
      })[0] || null
    )
  }

  PrescriptionUpload.prototype.hasVerifiedFile = function () {
    return this.entries.some(function (entry) {
      return entry.state === 'verified'
    })
  }

  PrescriptionUpload.prototype.isBusy = function () {
    return this.entries.some(function (entry) {
      return entry.state === 'uploading' || entry.state === 'verifying'
    })
  }

  PrescriptionUpload.prototype.isSatisfied = function () {
    return this.hasVerifiedFile() && this.isConsentGiven()
  }

  PrescriptionUpload.prototype.isConsentGiven = function () {
    return !this.consent || this.consent.checked
  }

  PrescriptionUpload.prototype.occupiedSlots = function () {
    return this.entries.filter(function (entry) {
      return entry.state !== 'failed'
    }).length
  }

  PrescriptionUpload.prototype.announce = function (message, kind) {
    if (!this.status) {
      return
    }

    this.status.textContent = message || ''
    this.status.setAttribute('data-kind', kind || 'info')
  }

  PrescriptionUpload.prototype.showError = function (visible) {
    if (this.error) {
      this.error.style.display = visible ? 'block' : 'none'
    }
  }

  PrescriptionUpload.prototype.validate = function (file, contentType) {
    // No separate counter for the files earlier in this same selection: startUpload pushes its
    // entry synchronously, so occupiedSlots() has already grown by the time the next file is
    // checked. Adding one would count each of them twice.
    if (this.occupiedSlots() >= this.maxFiles) {
      return format(MESSAGES.tooMany, { max: this.maxFiles })
    }

    if (!file.size) {
      return format(MESSAGES.empty, { name: file.name })
    }

    if (file.size > this.maxBytes) {
      return format(MESSAGES.tooLarge, { name: file.name, max: Math.round(this.maxBytes / (1024 * 1024)) })
    }

    var allowed = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf']

    if (allowed.indexOf(contentType) === -1) {
      return format(MESSAGES.badType, { name: file.name })
    }

    return null
  }

  PrescriptionUpload.prototype.handleFiles = function (files) {
    var self = this

    files.forEach(function (file) {
      var contentType = resolveContentType(file)
      var problem = self.validate(file, contentType)

      if (problem) {
        self.announce(problem, 'error')

        return
      }

      self.startUpload(file, contentType)
    })
  }

  PrescriptionUpload.prototype.startUpload = function (file, contentType) {
    var entry = {
      id: randomId(),
      file: file,
      contentType: contentType,
      state: 'uploading',
      progress: 0,
      token: null,
      message: MESSAGES.uploading,
      // Safari decodes HEIC, other browsers do not — so every image gets a preview attempt and the
      // <img> onerror below swaps in the document tile where the decode fails.
      previewUrl: contentType.indexOf('image/') === 0 ? URL.createObjectURL(file) : null
    }

    this.entries.push(entry)
    this.showError(false)
    this.render()
    this.upload(entry)
  }

  /**
   * Three steps, and the order is what keeps a half-finished upload from becoming a lie: the app
   * signs a URL for exactly this size and type, the bytes go straight to S3, and only then does
   * the app confirm and check what actually arrived.
   */
  PrescriptionUpload.prototype.upload = function (entry) {
    var self = this

    entry.state = 'uploading'
    entry.progress = 0
    entry.message = MESSAGES.uploading
    this.render()
    this.announce(MESSAGES.uploading, 'info')

    var body = JSON.stringify({
      uploadSessionId: this.uploadSessionId,
      contentType: entry.contentType,
      contentLength: entry.file.size,
      consentVersion: this.consentVersion,
      // Recorded at upload time, which is when the customer actually ticked the box. Sent as UTC
      // so a phone with a wrong timezone still lands on a comparable instant.
      consentGivenAt: new Date().toISOString()
    })

    return request('POST', this.endpoint + '/upload-url', {
      headers: { 'Content-Type': 'application/json' },
      body: body,
      timeout: 20000
    })
      .then(function (responseText) {
        var issued = JSON.parse(responseText)
        entry.token = issued.token

        // Exactly the headers the server signed — no more, no fewer. The signature covers the two
        // x-amz-server-side-encryption* headers as well as content-type, and S3 answers 403
        // SignatureDoesNotMatch if any of them is missing, added, or off by a byte. That is why
        // the set is taken from the response rather than written out here: guessing it means
        // breaking the upload the next time the server changes how it signs.
        return request('PUT', issued.uploadUrl, {
          headers: issued.headers || { 'Content-Type': entry.contentType },
          body: entry.file,
          timeout: 180000,
          onProgress: function (ratio) {
            entry.progress = ratio
            self.renderEntry(entry)
          },
          onStart: function (xhr) {
            entry.xhr = xhr
          }
        })
      })
      .then(function () {
        entry.state = 'verifying'
        entry.progress = 1
        entry.message = MESSAGES.verifying
        self.render()
        self.announce(MESSAGES.verifying, 'info')

        return request('POST', self.endpoint + '/confirm', {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: entry.token }),
          timeout: 30000
        })
      })
      .then(function () {
        entry.state = 'verified'
        entry.message = MESSAGES.done
        self.announce(MESSAGES.done, 'success')
        self.syncProperties()
        self.render()
      })
      .catch(function (error) {
        // A token that was issued but never confirmed is left alone on purpose: the server sweeps
        // those as orphans. Silently reusing the slot here would let a retry loop burn the cap.
        entry.state = 'failed'
        entry.message = error.message || MESSAGES.server
        self.announce(entry.message, 'error')
        self.syncProperties()
        self.render()
      })
  }

  PrescriptionUpload.prototype.removeEntry = function (entry) {
    if (entry.xhr && entry.state === 'uploading') {
      entry.xhr.abort()
    }

    if (entry.previewUrl) {
      URL.revokeObjectURL(entry.previewUrl)
    }

    this.entries = this.entries.filter(function (candidate) {
      return candidate !== entry
    })

    this.announce('', 'info')
    this.syncProperties()
    this.render()
  }

  PrescriptionUpload.prototype.writeProperty = function (name, value) {
    var input = document.createElement('input')

    input.type = 'hidden'
    input.name = 'properties[' + name + ']'
    input.value = value
    // Marks it as ours, so the next sync clears exactly what the last one wrote.
    input.setAttribute('data-prescription-property', '')

    this.form.appendChild(input)

    return input
  }

  /**
   * Writes the tokens into the add-to-cart form as line item properties, plus one line the
   * customer can actually read.
   *
   * The tokens are underscore-prefixed, so Shopify keeps them off the cart page, the checkout, the
   * order confirmation and the notification emails. The value is the token and nothing else — no
   * URL, no filename, nothing that identifies the file to anyone who can read order data.
   *
   * That secrecy leaves the customer with no sign that their prescription is attached once they
   * leave the product page, which is why a second, visible property carries a plain confirmation.
   * It is a label and nothing more: line item properties are entirely under the customer's
   * control, so this one proves nothing and the app never reads it — only the tokens are treated
   * as evidence that a file exists.
   */
  PrescriptionUpload.prototype.syncProperties = function () {
    if (!this.form) {
      return
    }

    var existing = this.form.querySelectorAll('[data-prescription-property]')

    Array.prototype.forEach.call(existing, function (node) {
      node.parentNode.removeChild(node)
    })

    var self = this
    var verified = this.entries.filter(function (entry) {
      return entry.state === 'verified' && entry.token
    })

    verified.forEach(function (entry, index) {
      // First file keeps the bare contract name; further files are numbered. Property names are
      // unique per line item, so several files cannot share one name.
      var input = self.writeProperty(self.propertyName + (index === 0 ? '' : '_' + (index + 1)), entry.token)

      input.setAttribute('data-prescription-token', '')
    })

    if (verified.length && this.statusProperty && this.statusText) {
      // Shopify drops a property with a blank value, so the count is appended to the text rather
      // than sent as a property of its own.
      var text = verified.length > 1 ? this.statusText + ' (' + verified.length + ' Dateien)' : this.statusText

      this.writeProperty(this.statusProperty, text)
    }

    this.renderGate()
  }

  PrescriptionUpload.prototype.renderEntry = function (entry) {
    var node = this.list && this.list.querySelector('[data-entry-id="' + entry.id + '"]')

    if (!node) {
      return
    }

    var bar = node.querySelector('[data-prescription-progress-bar]')

    if (bar) {
      bar.style.width = Math.round(entry.progress * 100) + '%'
    }

    node.setAttribute('data-state', entry.state)
    node.setAttribute('title', entry.file.name + ' — ' + entry.message)
  }

  PrescriptionUpload.prototype.render = function () {
    if (this.list) {
      var self = this

      this.list.innerHTML = ''

      this.entries.forEach(function (entry) {
        var item = document.createElement('div')
        item.className = 'preview-item'
        item.setAttribute('data-entry-id', entry.id)
        item.setAttribute('data-state', entry.state)
        item.setAttribute('title', entry.file.name + ' — ' + entry.message)

        if (entry.previewUrl && !entry.previewFailed) {
          var image = document.createElement('img')
          image.src = entry.previewUrl
          image.alt = entry.file.name
          image.onerror = function () {
            // A format this browser cannot decode — HEIC outside Safari, mostly. One flag, so the
            // re-render falls through to the document tile instead of looping on the same error.
            entry.previewFailed = true
            self.render()
          }
          item.appendChild(image)
        } else {
          var docTile = document.createElement('div')
          docTile.className = 'pdf-icon' + (isPdf(entry) ? '' : ' pdf-icon--generic')
          docTile.innerHTML = documentIcon(entry)
          var name = document.createElement('span')
          name.className = 'pdf-name'
          // textContent, never innerHTML: the file name is customer input and lands in the DOM.
          name.textContent = entry.file.name
          docTile.appendChild(name)
          item.appendChild(docTile)
        }

        var progress = document.createElement('span')
        progress.className = 'rx-upload__progress'
        var bar = document.createElement('span')
        bar.className = 'rx-upload__progress-bar'
        bar.setAttribute('data-prescription-progress-bar', '')
        bar.style.width = Math.round(entry.progress * 100) + '%'
        progress.appendChild(bar)
        item.appendChild(progress)

        var remove = document.createElement('button')
        remove.type = 'button'
        remove.className = 'remove-image'
        remove.setAttribute('data-prescription-remove', '')
        remove.setAttribute('aria-label', MESSAGES.remove + ': ' + entry.file.name)
        remove.textContent = '×'
        item.appendChild(remove)

        if (entry.state === 'failed') {
          var retry = document.createElement('button')
          retry.type = 'button'
          retry.className = 'rx-upload__retry'
          retry.setAttribute('data-prescription-retry', '')
          retry.textContent = MESSAGES.retry
          item.appendChild(retry)
        }

        self.list.appendChild(item)
      })
    }

    this.updateFileName()
    this.updateTriggerState()
    this.renderGate()
  }

  PrescriptionUpload.prototype.updateFileName = function () {
    if (!this.fileName) {
      return
    }

    var count = this.entries.length

    if (count === 0) {
      this.fileName.textContent = this.emptyLabel

      return
    }

    this.fileName.textContent = count === 1 ? MESSAGES.fileOne : format(MESSAGES.fileMany, { count: count })

    this.showError(false)
  }

  PrescriptionUpload.prototype.updateTriggerState = function () {
    if (!this.trigger) {
      return
    }

    this.trigger.disabled = this.occupiedSlots() >= this.maxFiles
  }

  /**
   * Only the state flag. The add-to-cart button is left alone on purpose: blindly clearing
   * `disabled` would hand a customer a button on a sold-out variant the theme had deliberately
   * locked, so the requirement is enforced at click and submit time instead — the same way
   * `product-file-upload` does it.
   */
  PrescriptionUpload.prototype.renderGate = function () {
    this.root.setAttribute('data-satisfied', this.isSatisfied() ? 'true' : 'false')
  }

  function init() {
    // Section rendering throws away the widget's markup but not the picker parked on <body>.
    var detached = document.querySelectorAll('[data-prescription-detached]')

    Array.prototype.forEach.call(detached, function (node) {
      var owner = node.__prescriptionRoot

      if (owner && !document.contains(owner)) {
        node.parentNode.removeChild(node)
      }
    })

    var roots = document.querySelectorAll(SELECTOR)

    Array.prototype.forEach.call(roots, function (root) {
      // The feature flag lives on the element, written by Liquid from a theme setting, so the flow
      // can be switched off from the theme editor without deploying the theme again.
      if (root.getAttribute('data-enabled') !== 'true' || root.__prescriptionUpload) {
        return
      }

      root.__prescriptionUpload = new PrescriptionUpload(root)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

  // Themes with section rendering swap the product form out from under us; re-initialise so the
  // widget and its gate come back instead of quietly disappearing.
  document.addEventListener('shopify:section:load', init)

  /**
   * The quick-add modal and the cart-drawer upsell fetch their markup from the Section Rendering
   * API and drop it in with innerHTML: no `shopify:section:load`, and the <script> tag that comes
   * with it never runs. Without this the widget would sit there inert in those forms — no upload,
   * and no gate keeping a prescription product out of the cart unchecked.
   */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes

        for (var j = 0; j < added.length; j++) {
          var node = added[j]

          if (node.nodeType === 1 && (node.matches(SELECTOR) || node.querySelector(SELECTOR))) {
            init()

            return
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true })
  }

  window.PrescriptionUpload = PrescriptionUpload
})()
